import { showToRolesMobile, showToRoles } from './show-to-roles';
import {
  containersTableDotsActions,
  containersTableDotsAllActions,
  containersTableDotsAllActionsWithTransfer,
  containersTableDotsClientActions,
  containersTableDotsSuperAdminActions,
  employeesThreeDots,
  imageActions,
  offersTableDotsClientReceivedActions,
  offersTableDotsClientSentAcceptedActions,
  offersTableDotsClientSentActions,
  offersTableDotsSuperAdminActions,
} from '../const';
import {
  ContainerGetAllQuery,
  EmployeeGetAllQuery,
  GateOutGetAllQuery,
  InvoiceGetAllQuery,
  OffersGetAllQuery,
  OfferStatus,
  OptionType,
  RepairGetAllQuery,
  User,
} from '../types';
import { i18n } from '../i18n';

export const withDisabledOptions = (
  isSale: boolean,
  isForSale: boolean,
  isHold: boolean,
  options: OptionType[],
) => {
  if (isHold) {
    options = options.map((option) =>
      [
        ...containersTableDotsActions,
        ...imageActions,
        { label: i18n.t('dropdown.edit'), value: 'edit' },
        { label: i18n.t('dropdown.repair'), value: 'repair' },
        { label: i18n.t('dropdown.releaseHold'), value: 'release-hold' },
      ].some((innerOption) => innerOption.value === option.value)
        ? option
        : { ...option, disabled: true },
    );
  }

  if (isSale) {
    options = options.map((option) =>
      [
        ...containersTableDotsActions,
        ...imageActions,
        ...(isForSale
          ? [{ label: i18n.t('dropdown.requestGateOut'), value: 'requestgateout' }]
          : []),
        { label: i18n.t('dropdown.edit'), value: 'edit' },
        { label: i18n.t('dropdown.repair'), value: 'repair' },
      ].some((innerOption) => innerOption.value === option.value)
        ? option
        : { ...option, disabled: true },
    );
  }

  options = options.sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled));

  return options;
};

export const getContainerOptions = (
  container: ContainerGetAllQuery['data'][number],
  user: User,
) => {
  const isAdmin = showToRoles('admin', user);
  const isSuper = showToRoles(['SUPER_ADMIN'], user);
  const canTransfer = showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user);
  const isGatedIn = container.status === 'GATED_IN';
  const isGatedOutRequested = container.status === 'GATE_OUT_REQUESTED';
  const isHold = !!container.isHold;
  let options: OptionType[];

  if (container.status === 'NOT_FOUND') {
    return [];
  }

  if (showToRoles(['YARD_COMPANY_VIEWER'], user)) {
    return [
      ...imageActions,
      { label: i18n.t('dropdown.printGateInReceipt'), value: 'print-gate-in-receipt' },
      {
        label: i18n.t('dropdown.printGateOutReceipt'),
        value: 'print-gate-out-receipt',
        disabled: container.status !== 'GATED_OUT',
      },
    ];
  }

  if (isAdmin) {
    if (canTransfer && isGatedIn) {
      options = isSuper
        ? containersTableDotsSuperAdminActions
        : containersTableDotsAllActionsWithTransfer;
    } else if (isGatedIn) {
      options = containersTableDotsAllActions;
    } else {
      options = containersTableDotsActions;
    }
  } else if (isGatedIn) {
    options = showToRolesMobile(['TRADER'], user)
      ? [containersTableDotsClientActions[1]]
      : containersTableDotsClientActions;
  } else {
    options = [];
  }

  if (!container.canTrade) {
    options = options.filter((option) => option.value !== 'sell');
  }

  if (isAdmin && (isGatedIn || isGatedOutRequested)) {
    options = [...options, { label: i18n.t('dropdown.repair'), value: 'repair' }];
  }

  if (isGatedIn) {
    if (isHold) {
      options = [...options, { label: i18n.t('dropdown.releaseHold'), value: 'release-hold' }];
    } else {
      options = [...options, { label: i18n.t('dropdown.holdContainer'), value: 'hold' }];
    }
  }

  options = [...options, ...imageActions];

  const finalOptions = withDisabledOptions(
    !!container.sale,
    container.sale?.status === 'FOR_SALE',
    isHold,
    options,
  );

  return [
    ...finalOptions,
    { label: i18n.t('dropdown.printGateInReceipt'), value: 'print-gate-in-receipt' },
    {
      label: i18n.t('dropdown.printGateOutReceipt'),
      value: 'print-gate-out-receipt',
      disabled: container.status !== 'GATED_OUT',
    },
  ];
};

export const getGateOutOptions = (container: GateOutGetAllQuery['data'][number], user: User) => {
  if (showToRoles(['YARD_COMPANY_VIEWER'], user)) return [];

  const isAdmin = showToRoles('admin', user);
  let options: OptionType[] = [];

  if (container.gateOut.status === 'EXPIRED') {
    options = [...options, { label: i18n.t('dropdown.extendDate'), value: 'extend-date' }];
  }

  if (isAdmin) {
    if (container.gateOut.status === 'ACTIVE') {
      options = [
        ...options,
        { label: i18n.t('dropdown.loadingAllowed'), value: 'loading-allowed' },
      ];
    }

    if (container.gateOut.status === 'LOADING_ALLOWED') {
      options = [
        ...options,
        { label: i18n.t('dropdown.loaded'), value: 'loaded' },
        { label: i18n.t('dropdown.complete'), value: 'complete' },
      ];
    }

    if (container.gateOut.status === 'LOADED') {
      options = [...options, { label: i18n.t('dropdown.complete'), value: 'complete' }];
    }
  }

  if (
    showToRoles(
      ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'CLIENT_MANAGER', 'CLIENT_DISPATCHER'],
      user,
    ) &&
    ['ACTIVE', 'LOADING_ALLOWED', 'LOADED', 'EXPIRED'].includes(container.gateOut.status)
  ) {
    options = [...options, { label: i18n.t('dropdown.delete'), value: 'delete' }];
  }

  return options;
};

export const getGateOutFilterOptions = (
  container: GateOutGetAllQuery['secondData'][number],
  user: User,
) => {
  if (showToRoles(['YARD_COMPANY_VIEWER'], user)) return [];

  const isAdmin = showToRoles('admin', user);
  let options: OptionType[] = [];

  if (container.status === 'ACTIVE' && isAdmin) {
    options = [...options, { label: i18n.t('dropdown.loadingAllowed'), value: 'loading-allowed' }];
  }

  if (container.status === 'LOADING_ALLOWED') {
    options = [...options, { label: i18n.t('dropdown.create'), value: 'create' }];
  }

  options = [...options, { label: i18n.t('dropdown.delete'), value: 'delete' }];

  return options;
};

export const getRepairOptions = (repair: RepairGetAllQuery['data'][number], user: User) => {
  if (showToRoles(['YARD_COMPANY_VIEWER'], user)) return [];

  const isAdmin = showToRoles('admin', user);
  let options: OptionType[] = [];

  if (isAdmin) {
    if (['DRAFT'].includes(repair.status)) {
      options = [{ label: i18n.t('dropdown.approve'), value: 'set-price' }];
    }

    if (['PENDING'].includes(repair.status)) {
      options = [...options, { label: i18n.t('dropdown.edit'), value: 'edit' }];
    }

    if (['ACTIVE'].includes(repair.status)) {
      options = [
        ...options,
        { label: i18n.t('dropdown.complete'), value: 'complete' },
        { label: i18n.t('dropdown.edit'), value: 'edit' },
      ];
    }

    if (['DRAFT', 'PENDING', 'ACTIVE'].includes(repair.status)) {
      options = [...options, { label: i18n.t('dropdown.delete'), value: 'delete' }];
    }
  } else {
    if (['PENDING'].includes(repair.status)) {
      options = [
        { label: i18n.t('dropdown.approve'), value: 'client-confirm' },
        { label: i18n.t('dropdown.reject'), value: 'client-reject' },
      ];
    }
  }

  return options;
};

export const getInvoiceOptions = (invoice: InvoiceGetAllQuery['data'][number], user: User) => {
  const isAdmin = showToRoles('admin', user);
  let options: OptionType[] = [];

  if (isAdmin && invoice.status === 'PENDING' && invoice.canPay) {
    options = [...options, { label: i18n.t('dropdown.markAsPaid'), value: 'paid' }];
  }

  if (
    isAdmin &&
    (invoice.status === 'PENDING' || invoice.status === 'DRAFT') &&
    invoice.canDelete &&
    invoice.mode !== 'AUTO'
  ) {
    options = [...options, { label: i18n.t('dropdown.delete'), value: 'delete' }];
  }

  return options;
};

export const getOfferOptionsBase = (
  offer: OffersGetAllQuery['data'][number],
  user: User,
  offerDirection: 'sent' | 'received',
) => {
  const isAdmin = showToRoles('admin', user);
  const isOfferReceived = offer.status === OfferStatus.RECEIVED;
  const isOfferRejected = offer.status === OfferStatus.REJECTED;
  const isOfferAccepted = offer.status === OfferStatus.ACCEPTED;
  const isOfferInstallment = offer.status === OfferStatus.SOLD_INST;

  if (isAdmin) {
    if (isOfferInstallment)
      return showToRoles(['SUPER_ADMIN'], user) ? [offersTableDotsSuperAdminActions[0]] : [];

    if (!isOfferAccepted) return [];

    if (showToRoles(['SUPER_ADMIN'], user)) return offersTableDotsSuperAdminActions;

    return offersTableDotsSuperAdminActions.slice(0, 2);
  }

  if (offerDirection === 'received') {
    return isOfferReceived ? offersTableDotsClientReceivedActions : [];
  }

  if (offerDirection === 'sent' && !isAdmin && (isOfferAccepted || isOfferInstallment)) {
    if (isOfferInstallment) return [offersTableDotsClientSentAcceptedActions[0]];

    return offersTableDotsClientSentAcceptedActions;
  }

  if (isOfferReceived || isOfferRejected) {
    return offersTableDotsClientSentActions;
  }

  return [];
};

export const getOfferOptions = (
  offer: OffersGetAllQuery['data'][number],
  user: User,
  offerDirection: 'sent' | 'received',
) => {
  if (offer.status === 'NOT_FOUND') return [];
  if (showToRoles(['YARD_COMPANY_VIEWER'], user)) return imageActions;

  let options = getOfferOptionsBase(offer, user, offerDirection);

  const isOfferInstallment = offer.status === OfferStatus.SOLD_INST;
  const isOfferPaid = offer.status === OfferStatus.CLOSED;

  if (showToRoles(['SUPER_ADMIN'], user) && (isOfferInstallment || isOfferPaid)) {
    options = [...options, { label: i18n.t('dropdown.cancelPaid'), value: 'cancel-paid' }];
  }

  return [...options, ...imageActions];
};

export const getEmployeeOptions = (
  employee: EmployeeGetAllQuery['data'][number],
  user: User,
  accountStatus: 'ACTIVE' | 'DEACTIVATED',
) => {
  let options: OptionType[] = [];

  if (
    showToRoles(
      ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_MANAGER'],
      user,
    ) &&
    employee.role === 'YardDispatcher'
  ) {
    options = [...options, { label: i18n.t('dropdown.edit'), value: 'EDIT' }];
  }

  if (
    showToRoles(
      [
        'SUPER_ADMIN',
        'YARD_COMPANY_ADMIN',
        'YARD_COMPANY_EMPLOYEE',
        'YARD_MANAGER',
        'CLIENT_MANAGER',
      ],
      user,
    )
  ) {
    if (accountStatus === 'ACTIVE') {
      options = [...options, ...employeesThreeDots.ACTIVE];
    } else {
      options = [...options, ...employeesThreeDots.DEACTIVATED];
    }
  }

  return options;
};
